import { useState } from 'react';
import type { MessageDoc, UserDoc } from '../../../shared/types';
import {
  EVENT_NAME_MAX,
  dayWords,
  daysUntil,
  findDate,
  findTime,
  suggestName,
  tileOf,
  whenWords,
  type PinEvent,
} from '../../../shared/events';
import { summaryOf } from '../../../shared/summary';
import type { WithId } from '../data';
import type { Pinned } from '../hooks';
import { QuoteThumb } from '../QuoteThumb';
import { BackCloses } from '../router';
import { EmptyState, IconClose, IconForward, IconPin, SheetHead } from '../ui';

/** The calendar tile an event is shown with: 'OCT' over '18'. */
export function EventTile({ day }: { day: string }) {
  const { month, date } = tileOf(day);
  return (
    <span className="event-tile" aria-hidden="true">
      <small>{month}</small>
      <b>{date}</b>
    </span>
  );
}

/**
 * Pinning a message, as an event or not.
 *
 * It finds the day and the time in the message where it can -- "Sunday the
 * 18th", "Oct 25", "2pm" -- and starts the name from its first line, so for
 * most messages pinning an event is a look and a tap. Pin without a date
 * keeps the plain pin, for something worth keeping that is not happening on
 * a day.
 */
export function PinSheet({
  message,
  users,
  today,
  onPin,
  onClose,
}: {
  message: WithId<MessageDoc>;
  users: Record<string, UserDoc>;
  today: string;
  onPin: (event?: PinEvent) => void;
  onClose: () => void;
}) {
  const text = message.data.text ?? '';
  const foundDay = findDate(text, today);
  const [name, setName] = useState(() => suggestName(text));
  const [day, setDay] = useState(() => foundDay ?? '');
  const [time, setTime] = useState(() => findTime(text) ?? '');
  const [remind, setRemind] = useState(true);
  const ready = name.trim() !== '' && day !== '' && day >= today;
  const who = users[message.data.authorUid]?.displayName ?? 'Someone';

  return (
    <div className="photo-sheet-scrim" onClick={onClose}>
      <BackCloses onBack={onClose} />
      <div
        className="photo-sheet pin-sheet"
        role="dialog"
        aria-label="Pin as an event"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="snap-sheet-grip" aria-hidden="true" />
        <SheetHead title="Pin as an event" onClose={onClose} />
        <div className="pin-sheet-quote">
          <span className="reply-rail-tick" />
          <QuoteThumb message={message} size={28} />
          <span className="pin-sheet-quote-text">
            <span className="reply-rail-who">{who}</span> {summaryOf(message.data)}
          </span>
        </div>

        <label className="pin-field" htmlFor="pin-name">
          <span>Name</span>
          <input
            id="pin-name"
            value={name}
            maxLength={EVENT_NAME_MAX}
            placeholder="Daisy’s party"
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <div className="pin-when">
          <label className="pin-field" htmlFor="pin-day">
            <span>Day</span>
            <input id="pin-day" type="date" min={today} value={day} onChange={(event) => setDay(event.target.value)} />
          </label>
          <label className="pin-field" htmlFor="pin-time">
            <span>Time</span>
            <input id="pin-time" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          </label>
        </div>
        {foundDay && day === foundDay && <span className="pin-found">Found in the message: {dayWords(foundDay)}</span>}

        <label className="pin-remind" htmlFor="pin-remind">
          <span className="pin-remind-words">
            Remind everyone that morning
            <small>One notification at 8 AM on the day</small>
          </span>
          <input
            id="pin-remind"
            type="checkbox"
            role="switch"
            checked={remind}
            onChange={(event) => setRemind(event.target.checked)}
          />
        </label>

        <button
          className="btn pin-go"
          disabled={!ready}
          onClick={() => onPin({ name: name.trim(), day, ...(time ? { time } : {}), remind })}
        >
          Pin event
        </button>
        <button className="pin-plain" onClick={() => onPin()}>
          Pin without a date
        </button>
      </div>
    </div>
  );
}

/**
 * Everything pinned, soonest first: events this week, events later, then
 * plain pins. A row goes to its message; the cross takes it down.
 */
export function PinList({
  pins,
  users,
  today,
  onOpen,
  onUnpin,
  onClose,
}: {
  pins: Pinned[];
  users: Record<string, UserDoc>;
  today: string;
  onOpen: (messageId: string) => void;
  onUnpin?: (pinId: string) => void;
  onClose: () => void;
}) {
  const soon = pins.filter((pin) => pin.event && daysUntil(pin.event.day, today) < 7);
  const later = pins.filter((pin) => pin.event && daysUntil(pin.event.day, today) >= 7);
  const plain = pins.filter((pin) => !pin.event);
  const name = (uid: string) => users[uid]?.displayName ?? 'Someone';

  const row = (pin: Pinned) => {
    const title = pin.event ? pin.event.name : `${name(pin.message.data.authorUid)}: ${summaryOf(pin.message.data) || 'a message'}`;
    return (
      <div className="pin-item" key={pin.id} data-pin={pin.id}>
        <button
          className="pin-item-open"
          onClick={() => {
            onClose();
            onOpen(pin.message.id);
          }}
        >
          {pin.event ? (
            <EventTile day={pin.event.day} />
          ) : (
            <span className="pin-item-mark" aria-hidden="true">
              <IconPin size={16} />
            </span>
          )}
          {!pin.event && <QuoteThumb message={pin.message} size={28} />}
          <span className="pin-item-text">
            <span className="pin-item-name">{title}</span>
            {pin.event && <span className="pin-item-when">{whenWords(pin.event, today)}</span>}
            <span className="pin-item-by">Pinned by {name(pin.by)}</span>
          </span>
          <IconForward size={14} />
        </button>
        {onUnpin && (
          <button className="pinned-close" aria-label={`Unpin ${pin.event?.name ?? 'this'}`} onClick={() => onUnpin(pin.id)}>
            <IconClose size={14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="photo-sheet-scrim" onClick={onClose}>
      <BackCloses onBack={onClose} />
      <div
        className="photo-sheet pin-sheet"
        role="dialog"
        aria-label="Pinned"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="snap-sheet-grip" aria-hidden="true" />
        <SheetHead title="Coming up" count={pins.length || undefined} onClose={onClose} />
        {pins.length === 0 && (
          <EmptyState
            icon={<IconPin size={22} />}
            title="Nothing pinned yet"
            body="Hold a message and pin it to keep it up here."
          />
        )}
        {soon.length > 0 && (
          <section className="pin-group" aria-label="This week">
            <h3 className="pin-group-head">This week</h3>
            {soon.map(row)}
          </section>
        )}
        {later.length > 0 && (
          <section className="pin-group" aria-label="Later">
            <h3 className="pin-group-head">Later</h3>
            {later.map(row)}
          </section>
        )}
        {plain.length > 0 && (
          <section className="pin-group" aria-label="Pinned">
            <h3 className="pin-group-head">Pinned</h3>
            {plain.map(row)}
          </section>
        )}
      </div>
    </div>
  );
}
